import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import { connect } from "react-redux";
import DatePicker from "react-datepicker";
import { Button } from "reactstrap";
import PropTypes from "prop-types";
import _ from "lodash";
import clientPropType from "../../../prop-types/client";
import eventPropType from "../../../prop-types/event";
import filtersPropType from "../../../prop-types/eventFilters";
import { fetchList } from "../../../state/events/actions";
import { updateFilters } from "../../../state/filters/actions";
import EventsList from "../components/events-list";
import "../styles/events-list.scss";
import Loading from "../../../commons/loading/components/loading";
import FilterBadge from "../components/filter-badge";
import { CHILDREN_ASSOCIATION, ON_DEMAND } from "../../../constants";
import {
  getEnabledTabs,
  getStoredGeoLocation,
  isAppleAndMobile
} from "../../../utils/util";
import {
  getSportName,
  getCategoryName,
  getSchoolName,
  getEventTypeName,
  filterEvents,
  updateScheduledStartDate,
  updateScheduledEndDate,
  resetScheduledDates,
  updateOnDemandStartDate,
  updateOnDemandEndDate,
  resetOnDemandDates,
  clearSportFilter,
  clearEventTypeFilter,
  clearConferenceFilter,
  clearSchoolFilter,
  clearTextFilter,
  clearCategoryFilter
} from "../../../utils/list-helper";
import "react-datepicker/dist/react-datepicker.css";

const propTypes = {
  client: clientPropType.isRequired,
  status: PropTypes.string,
  tabName: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      tabName: PropTypes.string
    })
  }).isRequired,
  width: PropTypes.number.isRequired,
  // Map state to props
  events: PropTypes.arrayOf(eventPropType).isRequired,
  types: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      sport: PropTypes.number
    })
  ).isRequired,
  filters: filtersPropType.isRequired,
  isFetching: PropTypes.bool.isRequired,
  geoRestrictionEnabled: PropTypes.bool.isRequired,
  branding: PropTypes.object.isRequired,
  error: PropTypes.string.isRequired,
  isLocationAllowed: PropTypes.bool.isRequired,
  geoLocation: PropTypes.object,
  // Map dispatch to props
  fetchListAction: PropTypes.func.isRequired,
  updateFiltersAction: PropTypes.func.isRequired
};

const defaultProps = {
  status: "",
  geoLocation: null
};

class EventListContainer extends Component {
  componentDidMount() {
    const { geoRestrictionEnabled } = this.props;
    if (!geoRestrictionEnabled) {
      this.fetchEventList();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      match: {
        params: { tabName }
      },
      geoRestrictionEnabled,
      isLocationAllowed
    } = this.props;
    if (prevProps.match.params.tabName !== tabName) {
      // Core API checks client GeoRestriction settings on tab change
      this.fetchEventList();
    } else if (
      prevProps.isLocationAllowed.toString() !== isLocationAllowed.toString()
    ) {
      // After user allows or denies Geo Location, get event list if GeoRestriction is enabled
      if (geoRestrictionEnabled) {
        // Delay fetch list by 1000ms to ensure Geo Location is retrieved
        this.timerID = setTimeout(() => {
          this.fetchEventList();
        }, 1000);
      }
    }
  }

  componentWillUnmount() {
    const { geoRestrictionEnabled } = this.props;
    if (geoRestrictionEnabled) {
      clearTimeout(this.timerID);
    }
  }

  fetchEventList = (id = null) => {
    const {
      fetchListAction,
      status,
      client,
      tabName,
      geoLocation,
      isLocationAllowed
    } = this.props;

    const currLocation = getStoredGeoLocation(geoLocation);
    const fetchId = id || client.id;
    const filters = {
      status,
      clientId: fetchId,
      tabName,
      geoLocation: currLocation,
      locationAllowed: isLocationAllowed
    };
    fetchListAction(filters);
  };

  render() {
    const {
      events,
      client,
      client: { id, clientType, children, features },
      filters,
      tabName,
      branding,
      types,
      width,
      history,
      location,
      error,
      geoRestrictionEnabled,
      updateFiltersAction,
      isFetching
    } = this.props;

    const logoUrl = branding?.logoImg
      ? `https://dkzv15vwb753z.cloudfront.net/${branding.logoImg}`
      : "";

    const tabs = getEnabledTabs(client.features);
    if (tabName && !tabs.includes(tabName)) {
      return null;
    }

    const labels = {};
    if (tabName === ON_DEMAND) {
      labels.headerText = "On Demand";
      labels.noDataMsg = "No recent events";
    } else {
      labels.headerText = "Scheduled";
      labels.noDataMsg = "No events scheduled";
    }

    let sportName;
    const hasSportFilter = filters.sport !== 0;
    if (hasSportFilter) {
      sportName = getSportName(filters.sport, types);
    }

    let schoolName;
    const hasSchoolFilter = filters.clientId !== id;
    if (hasSchoolFilter) {
      schoolName = getSchoolName(
        client[CHILDREN_ASSOCIATION[clientType.toLowerCase()]],
        filters.clientId
      );
    }

    let conferenceName;
    if (filters.conferenceId) {
      conferenceName = getSchoolName(children, filters.conferenceId);
    }

    let eventTypeName;
    if (filters.eventType) {
      eventTypeName = getEventTypeName(filters.eventType);
    }

    let categoryName;
    if (filters.category) {
      categoryName = getCategoryName(filters.category, client.categories);
    }

    const filteredEvents = filterEvents(events, filters, client, tabName);

    return (
      <div
        className={`events-list pt-3 d-flex flex-column ${
          isAppleAndMobile() ? "px-2" : "pl-2"
        }`}
      >
        <div className="d-flex align-items-center">
          <h1 className="today-legend h3 pb-2">{labels.headerText}</h1>
          <div className="d-flex flex-wrap">
            {sportName && (
              <FilterBadge
                badgeName={sportName}
                onDeleteClick={() =>
                  clearSportFilter(
                    updateFiltersAction,
                    filters,
                    history,
                    location
                  )
                }
              />
            )}
            {eventTypeName && client.features?.ppv && (
              <FilterBadge
                badgeName={eventTypeName}
                onDeleteClick={() =>
                  clearEventTypeFilter(updateFiltersAction, filters)
                }
              />
            )}
            {conferenceName && (
              <FilterBadge
                badgeName={conferenceName}
                onDeleteClick={() =>
                  clearConferenceFilter(updateFiltersAction, filters)
                }
              />
            )}
            {schoolName && (
              <FilterBadge
                badgeName={schoolName}
                onDeleteClick={() =>
                  clearSchoolFilter(updateFiltersAction, filters, client.id)
                }
              />
            )}
            {filters && filters.text && (
              <FilterBadge
                badgeName={filters.text}
                onDeleteClick={() =>
                  clearTextFilter(updateFiltersAction, filters)
                }
              />
            )}
            {categoryName && (
              <FilterBadge
                badgeName={categoryName}
                onDeleteClick={() =>
                  clearCategoryFilter(
                    updateFiltersAction,
                    filters,
                    history,
                    location
                  )
                }
              />
            )}
          </div>
        </div>
        {labels.headerText === "Scheduled" && (
          <div className="ps-date-picker">
            <div className="ps-date-select">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>From</label>
              <DatePicker
                showPopperArrow={false}
                selected={filters.sStartDate}
                onChange={(date) =>
                  updateScheduledStartDate(date, updateFiltersAction, filters)
                }
                selectsStart
                startDate={filters.sStartDate}
                endDate={filters.sEndDate}
              />
            </div>
            <div className="ps-date-select">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>To</label>
              <DatePicker
                showPopperArrow={false}
                selected={filters.sEndDate}
                onChange={(date) =>
                  updateScheduledEndDate(date, updateFiltersAction, filters)
                }
                selectsEnd
                startDate={filters.sStartDate}
                endDate={filters.sEndDate}
              />
            </div>
            <div>
              <Button
                onClick={() =>
                  resetScheduledDates(updateFiltersAction, filters)
                }
                outline
                color="secondary"
              >
                Reset
              </Button>
            </div>
          </div>
        )}
        {labels.headerText === "On Demand" && (
          <div className="ps-date-picker">
            <div className="ps-date-select">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>From</label>
              <DatePicker
                showPopperArrow={false}
                selected={filters.oStartDate}
                onChange={(date) =>
                  updateOnDemandStartDate(date, updateFiltersAction, filters)
                }
                selectsStart
                startDate={filters.oStartDate}
                endDate={filters.oEndDate}
              />
            </div>
            <div className="ps-date-select">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>To</label>
              <DatePicker
                showPopperArrow={false}
                selected={filters.oEndDate}
                onChange={(date) =>
                  updateOnDemandEndDate(date, updateFiltersAction, filters)
                }
                selectsEnd
                startDate={filters.oStartDate}
                endDate={filters.oEndDate}
              />
            </div>
            <div>
              <Button
                onClick={() => resetOnDemandDates(updateFiltersAction, filters)}
                outline
                color="secondary"
              >
                Reset
              </Button>
            </div>
          </div>
        )}
        <Loading
          isFetching={isFetching}
          hasData={!_.isEmpty(filteredEvents)}
          hasError={!_.isEmpty(error)}
          noDataMsg={labels.noDataMsg}
          clientLogo={logoUrl}
        >
          {!_.isEmpty(types) && !isFetching && (
            <EventsList
              chunkEvents={_.chunk(filteredEvents, 50)}
              filteredEvents={filteredEvents}
              tabName={tabName}
              withAds={features && features.ads.googleBanner}
              width={width}
              logoUrl={logoUrl}
              isGeoEnabled={geoRestrictionEnabled}
            />
          )}
        </Loading>
      </div>
    );
  }
}

EventListContainer.propTypes = propTypes;
EventListContainer.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  events: _.orderBy(
    // tabName: "on-demand", "scheduled"
    state.events.events.lists[state.filters.filters.tabName],
    ["startTime"],
    [state.filters.filters.tabName === ON_DEMAND ? "desc" : "asc"]
  ),
  types: state.events.events.types,
  filters: state.filters.filters,
  geoLocation: state.client.location,
  branding: state.client.clientBranding,
  isFetching: state.events.events.isFetching,
  isLocationAllowed: state.client.isLocationAllowed,
  error: state.events.events.error
});

const mapDispatchToProps = {
  fetchListAction: fetchList,
  updateFiltersAction: updateFilters
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(EventListContainer);
